import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { CostCenter } from "../models/CostCenter";
import { COST_CENTER_INITIAL_VALUES } from "../models/CostCenter";
import { useCostCenters } from "../hooks/useCostCenters";
import {
	useDeleteCostCenter,
	useUpsertCostCenter,
} from "../hooks/useCostCenterMutation";
import { CostCentersTable } from "./CostCentersTable";
import { CostCenterDetailViewer } from "./CostCenterDetailViewer";
import { CostCentersToolbar } from "./CostCentersToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	costCenterId: urlParam.int(),
};

export const CostCentersManager = () => {
	// Filters and the selected record, all in the query string.
	const [{ search, onlyActives, costCenterId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [costCenterToDelete, setCostCenterToDelete] =
		useState<CostCenter | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "cost-centers-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { costCenters, isLoading, error: queryError } = useCostCenters();

	const filteredCostCenters = useMemo(() => {
		const base = onlyActives
			? costCenters.filter((c) => c.Active)
			: costCenters;
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter(
			(c) =>
				c.Name?.toLowerCase().includes(s) ||
				c.Description?.toLowerCase().includes(s) ||
				c.DeptId?.toLowerCase().includes(s) ||
				c.EndUser?.toLowerCase().includes(s),
		);
	}, [costCenters, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<CostCenter>({
		id: costCenterId,
		setId: (value) => setFilters({ costCenterId: value }),
		dropId: () => dropValues("costCenterId"),
		rows: costCenters,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedCostCenter = selection.current;

	const { upsertCostCenter, isUpserting } = useUpsertCostCenter({
		onSuccess: (data) => {
			const isNew = !selectedCostCenter?.Id;
			setNotification(
				isNew
					? "Cost center created successfully"
					: "Cost center updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteCostCenter, isDeleting } = useDeleteCostCenter({
		onSuccess: () => {
			setNotification("Cost center deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setCostCenterToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(record: CostCenter | null) => {
			if (record) selection.select(record);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((costCenter: CostCenter) => {
		setCostCenterToDelete(costCenter);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (costCenterToDelete?.Id) {
			deleteCostCenter(costCenterToDelete.Id);
		}
	}, [costCenterToDelete, deleteCostCenter]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setCostCenterToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...COST_CENTER_INITIAL_VALUES });
	}, []);

	const handleSave = useCallback(
		(costCenter: CostCenter) => {
			upsertCostCenter(costCenter);
		},
		[upsertCostCenter],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, []);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Cost Centers
			</Typography>

			<CostCentersToolbar
				search={searchText}
				onSearchChange={setSearchText}
				onlyActives={onlyActives}
				onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
				onAdd={handleAdd}
			/>

			<Group
				id="cost-centers-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={1}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<CostCentersTable
									costCenters={filteredCostCenters}
									selectedCostCenter={
										selectedCostCenter?.Id ? selectedCostCenter : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								overflowY: "scroll",
								overflowX: "hidden",
							}}
						>
							<CostCenterDetailViewer
								costCenter={selectedCostCenter}
								existingCostCenters={costCenters}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE COST CENTER"
				itemName={costCenterToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
